/** @format */

// ─────────────────────────────────────────────────────────────────────────
// Collect / AARRP frontend types — mirror server/src/collect models.
// Kept intentionally permissive on _id / timestamp shapes because they
// come through JSON from the API as strings.
// ─────────────────────────────────────────────────────────────────────────

export type BusinessRole = "owner" | "admin" | "member" | "viewer";

/** Stage 2G — conversational auto-reply engine configuration. */
export interface IAutoReplySettings {
    enabled?: boolean;
    maxPerThread?: number;
    minHoursBetween?: number;
    askForDateOnVague?: boolean;
    confirmPromise?: boolean;
    ackDispute?: boolean;
    ackAlreadyPaid?: boolean;
}

/** Stage 2I — per-recommendation-type auto-apply toggles. */
export interface IRecommendationAutoApply {
    reduceOutreach?: boolean;
    enableEarlyReminder?: boolean;
    offerPaymentPlan?: boolean;
    switchChannel?: boolean;
    escalateReview?: boolean;
    pauseAndReview?: boolean;
}

/** Stage 2I — recommendations engine configuration. */
export interface IRecommendationSettings {
    enabled?: boolean;
    autoApply?: IRecommendationAutoApply;
}

export interface IBusinessSettings {
    defaultAiTone?: "friendly" | "professional" | "firm";
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    autoSendEnabled?: boolean;
    manualApprovalRequired?: boolean;
    promiseGraceDays?: number;
    brokenPromiseThreshold?: number;
    escalationDaysPerStage?: number;
    manualReviewBalanceThreshold?: number;
    manualReviewDaysOverdueThreshold?: number;
    legalActionDaysOverdueThreshold?: number;
    autoReply?: IAutoReplySettings;
    recommendations?: IRecommendationSettings;
    [key: string]: any;
}

export interface IBusiness {
    _id: string;
    name: string;
    timezone?: string;
    /** ISO-3166 alpha-2 billing country (US | CA | GB). Drives billing currency. */
    country?: string;
    /** Billing currency derived from country (usd | cad | gbp). */
    currency?: string;
    settings?: IBusinessSettings;
    isActive?: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface IBusinessMembership {
    _id: string;
    businessId: string;
    userId: string;
    role: BusinessRole;
    status?: string;
    business?: IBusiness;
    createdAt?: string;
    updatedAt?: string;
}

// ── Super-admin console (platform-wide business listing + impersonation) ──
export interface IAdminBusinessCard {
    _id: string;
    name: string;
    country?: string;
    currency?: string;
    isActive: boolean;
    createdAt?: string;
    owner: { name?: string; email?: string } | null;
    subscription: { status?: string; planPhase?: string } | null;
    metrics: {
        openInvoiceCount: number;
        outstandingBalance: number;
        memberCount: number;
    };
}

export interface IAdminBusinessListPage {
    data: IAdminBusinessCard[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

/** Active super-admin impersonation context (viewing another business). */
export interface IImpersonationContext {
    active: boolean;
    businessId: string | null;
    businessName: string | null;
}

export type QuickBooksEnvironment = "sandbox" | "production" | "unknown";

export interface ICollectSystemInfo {
    quickBooksEnvironment: QuickBooksEnvironment;
}

// ── QuickBooks connection ────────────────────────────────────────────────
export type QuickBooksConnectionStatus =
    | "active"
    | "disconnected"
    | "expired"
    | "error";

export interface IQuickBooksConnection {
    _id: string;
    businessId: string;
    realmId: string;
    environment?: Exclude<QuickBooksEnvironment, "unknown">;
    status: QuickBooksConnectionStatus;
    companyName?: string;
    connectedAt?: string;
    disconnectedAt?: string;
    lastSyncAt?: string;
    lastError?: string | null;
}

export interface IQuickBooksConnectStartResponse {
    authorizationUrl: string;
    environment: Exclude<QuickBooksEnvironment, "unknown">;
}

// ── Sync jobs ────────────────────────────────────────────────────────────
export type SyncJobType = "customers" | "invoices" | "all";
export type SyncJobStatus =
    | "queued"
    | "running"
    | "completed"
    | "failed"
    | "partial";

export interface ISyncJob {
    _id: string;
    businessId: string;
    type: SyncJobType;
    status: SyncJobStatus;
    startedAt?: string;
    finishedAt?: string;
    itemsProcessed?: number;
    itemsFailed?: number;
    error?: string | null;
    metadata?: Record<string, any>;
}

// ── Customers ────────────────────────────────────────────────────────────
export interface ICollectCustomer {
    _id: string;
    businessId: string;
    qboCustomerId?: string;
    displayName: string;
    companyName?: string;
    email?: string;
    phone?: string;
    balance?: number;
    currency?: string;
    isActive?: boolean;    // Stage 2D — customer opted out via reply UNSUBSCRIBE intent
    communicationOptedOut?: boolean;
    communicationOptedOutAt?: string;
    communicationOptedOutReason?: string;
    // Stage 2F — SMS-specific opt-out (independent of email opt-out).
    smsOptedOut?: boolean;
    smsOptedOutAt?: string;
    smsOptedOutKeyword?: string;
    smsOptInAt?: string;
    primaryPhone?: string;
    mobilePhone?: string;
    createdAt?: string;
    updatedAt?: string;
}

// ── Recovery state machine (Stage 2A) ───────────────────────────────────
export type RecoveryState =
    | "monitoring"
    | "due_soon"
    | "due_today"
    | "overdue"
    | "active_recovery"
    | "promise_to_pay"
    | "partially_paid"
    | "disputed"
    | "manual_review"
    | "legal_action"
    | "recovered"
    | "closed";

export type RecoveryTransitionSource =
    | "user_manual"
    | "system_reconciler"
    | "system_sync"
    | "webhook"
    | "promise_service"
    | "escalation_engine"
    | "reply_classifier";

export type RecoveryActorType = "user" | "system" | "webhook" | "ai";

export type EscalationLevel =
    | "friendly"
    | "professional"
    | "firm"
    | "manual_review";

export interface IRecoveryStateTransition {
    _id: string;
    businessId: string;
    invoiceId: string;
    fromState: RecoveryState | null;
    toState: RecoveryState;
    source: RecoveryTransitionSource;
    actorType: RecoveryActorType;
    actorUserId?:
    | string
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    }
    | null;
    reason?: string;
    metadata?: Record<string, any>;
    createdAt: string;
}

export interface IRecoveryStateMetadata {
    value: RecoveryState;
    label: string;
    allowedTransitions: Array<{ value: RecoveryState; label: string }>;
}

export interface ITransitionInvoiceStatePayload {
    to: RecoveryState;
    reason?: string;
}

// ── Promise-to-Pay (Stage 2B) ───────────────────────────────────────────
export type PromiseStatus = "pending" | "kept" | "broken" | "cancelled";
export type PromiseSource = "manual" | "reply" | "customer_portal";

export interface IPromiseToPay {
    _id: string;
    businessId: string;
    invoiceId: string;
    customerId?: string;
    promisedAmount: number;
    currency: string;
    promisedDate: string;
    gracePeriodDays: number;
    status: PromiseStatus;
    source: PromiseSource;
    createdByUserId?:
    | string
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    }
    | null;
    note?: string;
    keptAt?: string;
    brokenAt?: string;
    cancelledAt?: string;
    resolutionReason?: string;
    createdAt?: string;
    updatedAt?: string;
    /** Populated invoice summary (business-level promises list only). */
    invoice?: {
        _id: string;
        docNumber?: string;
        customerDisplayName?: string;
        balance?: number;
        currency?: string;
        status?: string;
        dueDate?: string;
    };
    /** Populated customer summary (business-level promises list only). */
    customer?: {
        _id: string;
        displayName?: string;
        primaryEmail?: string;
    };
}

export interface ICreatePromisePayload {
    promisedAmount: number;
    promisedDate: string; // ISO date or YYYY-MM-DD
    note?: string;
    source?: PromiseSource;
}

export interface IResolvePromisePayload {
    reason?: string;
}

export interface IListPromisesQuery {
    status?: PromiseStatus;
    cursor?: string;
    limit?: number;
}

// ── Recovery Queue + Escalation (Stage 2C) ──────────────────
export interface IRecoveryQueueQuery {
    state?: RecoveryState | RecoveryState[];
    escalationLevel?: EscalationLevel | EscalationLevel[];
    assignedToUserId?: string | "unassigned";
    minBalance?: number;
    minDaysOverdue?: number;
    cursor?: string;
    limit?: number;
}

export interface IRecoveryQueuePage {
    items: ICollectInvoice[];
    nextCursor: string | null;
    count: number;
}

export interface IRecoveryQueueSummary {
    byState: Array<{
        state: RecoveryState;
        count: number;
        totalBalance: number;
    }>;
    byEscalationLevel: Array<{
        level: EscalationLevel;
        count: number;
        totalBalance: number;
    }>;
    totals: {
        count: number;
        totalBalance: number;
        avgPriorityScore: number;
    };
}

export interface IAssignInvoicePayload {
    assigneeUserId: string | null;
}

export interface IEscalationDecision {
    invoiceId: string;
    nextLevel: EscalationLevel | null;
    nextState: RecoveryState | null;
    priorityScore: number;
    riskScore: number;
    reasons: string[];
}

// ── Invoices ─────────────────────────────────────────────────────────────
export type CollectInvoiceStatus =
    | "draft"
    | "open"
    | "partially_paid"
    | "paid"
    | "overdue"
    | "void";

export interface IInvoiceLineItem {
    description?: string;
    amount: number;
    quantity?: number;
    unitPrice?: number;
}

export interface ICollectInvoice {
    _id: string;
    businessId: string;
    // Backend uses qboId; older slices used qboInvoiceId — keep both for compat.
    qboId?: string;
    qboInvoiceId?: string;
    docNumber?: string;
    customerId: string;
    customer?: ICollectCustomer;
    customerDisplayName?: string;
    qboCustomerId?: string;
    currency?: string;
    totalAmount: number;
    balance: number;
    status: CollectInvoiceStatus;
    // Backend stores transaction date as `txnDate`. `issueDate` kept as alias.
    txnDate?: string;
    issueDate?: string;
    dueDate?: string;
    daysOverdue?: number;
    agingBucket?: string;
    lineItems?: IInvoiceLineItem[];
    privateNote?: string;
    customerMemo?: string;
    emailStatus?: string;
    lastReminderAt?: string;
    lastSyncedAt?: string;
    // Recovery state machine (Stage 2A)
    recoveryState?: RecoveryState;
    recoveryStateEnteredAt?: string;
    escalationLevel?: EscalationLevel;
    escalationLevelSetAt?: string;
    assignedToUserId?:
    | string
    | {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    }
    | null;
    priorityScore?: number;
    riskScore?: number;
    lastCustomerReplyAt?: string;
    brokenPromiseCount?: number;
    disputeOpen?: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface IArAgingBucket {
    label: string; // e.g. "current", "1_30", "31_60", "61_90", "90_plus"
    count: number;
    total: number;
}

export interface IArAgingSummary {
    currency: string;
    totalOutstanding: number;
    invoiceCount: number;
    buckets: IArAgingBucket[];
    generatedAt?: string;
}

// ── Dashboard overview (one-shot widget data) ────────────────────────────
export type UrgencyTier = "critical" | "high" | "medium" | "low";

export interface IDashboardUrgencyBucket {
    count: number;
    totalBalance: number;
    label: string;
}

export interface IDashboardStageBucket {
    count: number;
    totalBalance: number;
}

export interface IDashboardOverview {
    /** Top N invoices sorted by priorityScore desc. */
    topUrgentInvoices: ICollectInvoice[];
    /** Counts + open balance grouped by RecoveryState. */
    workflowStages: Record<string, IDashboardStageBucket>;
    /** Counts + open balance grouped by urgency tier. */
    urgencyBuckets: Record<UrgencyTier, IDashboardUrgencyBucket>;
    /** Total number of open/overdue invoices in the urgency rollup. */
    openInvoiceCount: number;
    generatedAt: string;
}

// ── Collections metrics (money in / out / expected) ──────────────────────
export type CollectionsRange = "2w" | "4w" | "3m" | "6m" | "12m";

export interface ICollectionsBucket {
    amount: number;
    count: number;
}

export interface ICollectionsMetrics {
    range: CollectionsRange;
    rangeLabel: string;
    rangeStart: string;
    monthStart: string;
    currency: string;
    /** QBO cash payments applied within the selected range. */
    collected: ICollectionsBucket;
    /** QBO cash payments applied since the 1st of the current month. */
    monthCollected: ICollectionsBucket;
    /** Open balance overdue with a due date in the current month. */
    overdueCurrentMonth: ICollectionsBucket;
    /** All open balance currently past due (any due date). */
    overdue: ICollectionsBucket;
    /** All open balance still owed ("what's left"). */
    outstanding: ICollectionsBucket;
    /** Pending promise-to-pay total ("expected"). */
    promises: ICollectionsBucket;
    /** collected (range) + outstanding. */
    total: number;
    generatedAt: string;
}

// ── KPI card drill-down (dashboard breakdown modal) ──────────────────────
/** Which dashboard KPI card a breakdown belongs to. */
export type KpiBreakdownMetric =
    | "outstanding"
    | "overdue"
    | "collected"
    | "promises";

/** Per-customer payer history surfaced in the breakdown rows. */
export interface IKpiPayerHistory {
    segment?: CustomerSegment;
    attentionScore?: number;
    /** On-time payment rate 0–1. */
    onTimeRate?: number;
    /** Avg signed days to pay (negative = early). */
    avgDaysToPay?: number;
    invoicesPaid?: number;
    invoicesTotal?: number;
    onTimePayments?: number;
    latePayments?: number;
    promisesKept?: number;
    promisesBroken?: number;
}

/** One row in a KPI breakdown — an invoice or a promise, normalized. */
export interface IKpiBreakdownItem {
    /** Present for invoice-backed rows (outstanding/overdue/collected). */
    invoiceId?: string | null;
    /** Present for promise rows. */
    promiseId?: string;
    docNumber?: string;
    customerId?: string;
    customerDisplayName?: string;
    currency: string;
    /** The headline amount for this row (balance, total, or promised). */
    amount: number;
    balance?: number;
    totalAmount?: number;
    status?: CollectInvoiceStatus;
    dueDate?: string;
    daysOverdue?: number;
    agingBucket?: string;
    recoveryState?: RecoveryState;
    escalationLevel?: EscalationLevel;
    priorityScore?: number;
    brokenPromiseCount?: number;
    disputeOpen?: boolean;
    lastCustomerReplyAt?: string;
    /** collected only — when the invoice was marked recovered. */
    recoveredAt?: string;
    /** promises only. */
    promisedDate?: string;
    promiseOverdue?: boolean;
    payerHistory?: IKpiPayerHistory | null;
}

export interface IKpiBreakdownSummary {
    totalAmount: number;
    count: number;
    /** outstanding only — past-due portion of the total. */
    overdueAmount?: number;
    /** promises only — how many pending promises are already past due. */
    overdueCount?: number;
    /** collected only — selected payment window. */
    rangeStart?: string;
    rangeLabel?: string;
}

export interface IKpiBreakdown {
    metric: KpiBreakdownMetric;
    currency: string;
    summary: IKpiBreakdownSummary;
    items: IKpiBreakdownItem[];
}

// ── Autopilot summary (dashboard "is this handled?" panel) ───────────────

export interface IAutopilotState {
    /** True when the orchestrator kill-switch conditions all pass. */
    enabled: boolean;
    /** "autopilot" = auto-send; "approval" = drafts wait for a human. */
    mode: "autopilot" | "approval";
    autoSendEnabled: boolean;
    emailEnabled: boolean;
    smsEnabled: boolean;
    manualApprovalRequired: boolean;
    activeCampaignCount: number;
    quickBooksConnected: boolean;
    /** Human-readable reason autopilot is off, or null when on. */
    reason: string | null;
}

export interface IAutopilotPortfolio {
    workingCount: number;
    monitoringCount: number;
    attentionCount: number;
    deliveryIssues: number;
}

export interface IAutopilotAttentionBreakdown {
    manualReview: number;
    disputes: number;
    brokenPromises: number;
    verifyPayment: number;
    replyReview: number;
    other: number;
}

export interface IAutopilotLastAction {
    at: string;
    title: string;
    type: string;
    severity: string;
}

export interface IAutopilotSummary {
    autopilot: IAutopilotState;
    portfolio: IAutopilotPortfolio;
    attentionBreakdown: IAutopilotAttentionBreakdown;
    lastAction: IAutopilotLastAction | null;
    next7Days: {
        promisesDue: number;
        invoicesComingDue: number;
    };
    currency: string;
    generatedAt: string;
}

// ── Activity feed ("What we did for you") ────────────────────────────────

export interface IActivityFeedItem {
    _id: string;
    businessId: string;
    invoiceId?: string;
    customerId?: string;
    type: string;
    severity: "info" | "success" | "warning" | "critical";
    title: string;
    description?: string;
    actorType: "user" | "system" | "ai" | "webhook" | "customer";
    actorDisplayName?: string;
    metadata?: Record<string, any>;
    occurredAt: string;
}

export interface IActivityFeedPage {
    items: IActivityFeedItem[];
    nextBefore: string | null;
    count: number;
}

// ── Action Center (dashboard triage queue) ──────────────────────────────

export interface IActionCenterRecommended {
    code: string;
    label: string;
    hint: string;
}

export interface IActionCenterItem {
    taskId: string;
    type: string;
    title: string;
    description?: string;
    priority: "low" | "medium" | "high" | "urgent";
    status: "open" | "in_progress" | "done" | "cancelled";
    assigned: boolean;
    assignedToUserId?: string;
    createdAt?: string;
    dueAt?: string;
    ageDays: number;
    recommendedAction: IActionCenterRecommended;
    invoiceId?: string;
    docNumber?: string;
    customerId?: string;
    customerDisplayName?: string;
    currency?: string;
    balance?: number;
    daysOverdue?: number;
    recoveryState?: string;
    brokenPromiseCount?: number;
    disputeOpen?: boolean;
}

export interface IActionCenterSummary {
    total: number;
    urgent: number;
    high: number;
    medium: number;
    low: number;
}

export interface IActionCenter {
    summary: IActionCenterSummary;
    items: IActionCenterItem[];
    generatedAt: string;
}

// ── Recovery Impact scorecard ─────────────────────────────────

export type ImpactRange = "2w" | "4w" | "3m" | "6m" | "12m";

export interface IRecoveryImpactBucket {
    amount: number;
    count: number;
}

export interface IRecoveryImpact {
    range: ImpactRange;
    rangeLabel: string;
    rangeStart: string;
    previousRangeStart: string;
    currency: string;
    recovered: IRecoveryImpactBucket;
    previousRecovered: IRecoveryImpactBucket;
    recoveredDeltaPct: number | null;
    avgDaysToGetPaid: number | null;
    previousAvgDaysToGetPaid: number | null;
    avgDaysImprovement: number | null;
    onTimeRate: number | null;
    generatedAt: string;
}

// ── Relationship Safety panel ────────────────────────────────

export interface IRelationshipShielded {
    total: number;
    promiseToPay: number;
    disputed: number;
    manualReview: number;
}

export interface IRelationshipTone {
    friendly: number;
    professional: number;
    firm: number;
    total: number;
    gentlePct: number | null;
}

export interface IRelationshipOptOuts {
    total: number;
    email: number;
    sms: number;
}

export interface IRelationshipConcerns {
    disputes: number;
    complaints: number;
    total: number;
}

export interface IRelationshipReliable {
    count: number;
    avgOnTimeRate: number | null;
}

export interface IRelationshipSafety {
    shielded: IRelationshipShielded;
    tone: IRelationshipTone;
    optOuts: IRelationshipOptOuts;
    concerns: IRelationshipConcerns;
    reliable: IRelationshipReliable;
    generatedAt: string;
}


export interface IRecoveryFunnelStage {
    /** RecoveryState enum value (e.g. "overdue", "recovered"). */
    state: string;
    count: number;
    balance: number;
}

export interface IRecoveryFunnel {
    currency: string;
    stages: IRecoveryFunnelStage[];
    totalCount: number;
    recoveredCount: number;
    openCount: number;
    openBalance: number;
    generatedAt: string;
}

export interface IRecoveryHealthComponents {
    /** Share of tracked invoices already recovered (0–100). */
    collectedRatio: number;
    /** 100 when none of the open balance is overdue (0–100). */
    overduePressure: number;
    /** Kept vs resolved promises (0–100). */
    promiseReliability: number;
    /** 100 when nothing needs a human (0–100). */
    manualReviewLoad: number;
}

export interface IRecoveryHealth {
    /** Composite 0–100 collections-health score. */
    healthScore: number;
    components: IRecoveryHealthComponents;
    currency: string;
    openBalance: number;
    overdueBalance: number;
    pendingPromiseAmount: number;
    manualReviewCount: number;
    generatedAt: string;
}

// One persisted daily snapshot point, oldest → newest, used to plot trends.
export interface IRecoveryTrendPoint {
    date: string;
    healthScore: number;
    openBalance: number;
    overdueBalance: number;
    recoveredAmount: number;
    pendingPromiseAmount: number;
    openCount: number;
    overdueCount: number;
}

export interface IRecoveryTrends {
    days: number;
    points: IRecoveryTrendPoint[];
    generatedAt: string;
}

export type DunningRuleTrigger = "before_due" | "due_today" | "after_due";
export type DunningChannel = "email" | "sms" | "both";

export interface IDunningRule {
    _id?: string;
    name: string;
    trigger: DunningRuleTrigger;
    offsetDays: number;
    channel: DunningChannel;
    templateId: string;
    minBalance?: number;
    isActive: boolean;
}

export interface IDunningCampaign {
    _id: string;
    businessId: string;
    name: string;
    description?: string;
    isDefault: boolean;
    isActive: boolean;
    rules: IDunningRule[];
    createdAt?: string;
    updatedAt?: string;
}

export type TemplateChannel = "email" | "sms";
export type TemplateCategory =
    | "upcoming"
    | "due_today"
    | "overdue"
    | "payment_received"
    | "generic";

export interface IReminderTemplate {
    _id: string;
    businessId: string;
    name: string;
    channel: TemplateChannel;
    category: TemplateCategory;
    subject: string;
    body: string;
    isSystem: boolean;
    isActive: boolean;
    // Stage 2D — optional tone tag (FRIENDLY / PROFESSIONAL / FIRM / MANUAL_REVIEW)
    // matched against an invoice's current escalationLevel by the orchestrator.
    escalationLevel?: EscalationLevel | null;
    createdAt?: string;
    updatedAt?: string;
}

export type DunningEventStatus = "sent" | "failed" | "skipped";
export type DunningEventTrigger = "scheduled" | "manual";

export interface IDunningEvent {
    _id: string;
    businessId: string;
    invoiceId: string;
    customerId?: string;
    campaignId?: string;
    ruleId?: string;
    templateId?: string;
    trigger: DunningEventTrigger;
    channel: TemplateChannel;
    status: DunningEventStatus;
    recipient?: string;
    subject?: string;
    // Backend stores a short preview of the rendered body. Keep `body` as
    // an alias for older callers.
    bodyPreview?: string;
    body?: string;
    providerMessageId?: string;
    messageId?: string;
    errorMessage?: string;
    error?: string;
    sentAt: string;
}

export interface IWebhookEvent {
    _id: string;
    type: string;
    createdAt: string;
    metadata?: Record<string, any>;
}

export type InvoiceTimelineEntryKind = "dunning" | "webhook";

export interface IInvoiceTimelineEntry {
    kind: InvoiceTimelineEntryKind;
    occurredAt: string;
    data: IDunningEvent | IWebhookEvent;
}

// ── Generic pagination shape used by Collect list endpoints ──────────────
export interface ICollectPagedResponse<T> {
    items: T[];
    nextCursor?: string | null;
    total?: number;
}

// ── Payloads ─────────────────────────────────────────────────────────────
export interface ICreateBusinessPayload {
    name: string;
    timezone?: string;
}

export interface IUpdateBusinessSettingsPayload {
    name?: string;
    timezone?: string;
    defaultAiTone?: "friendly" | "professional" | "firm";
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    autoSendEnabled?: boolean;
    manualApprovalRequired?: boolean;
    // Recovery & escalation policy (Stage 2D). Consumed by the escalation
    // engine + promise watchdog; persisted under business.settings.
    promiseGraceDays?: number;
    brokenPromiseThreshold?: number;
    escalationDaysPerStage?: number;
    manualReviewBalanceThreshold?: number;
    manualReviewDaysOverdueThreshold?: number;
    legalActionDaysOverdueThreshold?: number;
    // Stage 2G — conversational auto-reply engine.
    autoReply?: IAutoReplySettings;
    // Stage 2I — behaviour-driven recommendations engine.
    recommendations?: IRecommendationSettings;
    // Loose escape hatch for forward-compatible fields.
    settings?: Record<string, any>;
}

export interface IUpsertCampaignPayload {
    name: string;
    description?: string;
    isDefault?: boolean;
    isActive?: boolean;
    rules: IDunningRule[];
}

export interface IUpsertTemplatePayload {
    name?: string;
    channel?: TemplateChannel;
    category?: TemplateCategory;
    subject?: string;
    body?: string;
    isActive?: boolean;
    // Stage 2D — tone tag. Send `null` to clear on update; system templates
    // reject changes to this field.
    escalationLevel?: EscalationLevel | null;
}

export interface ISendManualReminderPayload {
    templateId: string;
    recipientOverride?: string;
}

export interface IListInvoicesQuery {
    status?: CollectInvoiceStatus | CollectInvoiceStatus[];
    customerId?: string;
    q?: string;
    cursor?: string;
    limit?: number;
}

export interface IListCustomersQuery {
    q?: string;
    cursor?: string;
    limit?: number;
}

export interface IListTemplatesQuery {
    channel?: TemplateChannel;
    category?: TemplateCategory;
    includeInactive?: boolean;
}

export interface IListDunningEventsQuery {
    invoiceId?: string;
    status?: DunningEventStatus;
    cursor?: string;
    limit?: number;
}

// ──────────────────────────────────────────────────────────────────────
// Stage 2D — Customer reply ingestion (AI-classified inbound responses)
// ──────────────────────────────────────────────────────────────────────
export type ReplyChannel = "email" | "sms";

export type ReplyIntent =
    | "payment_commitment"
    | "already_paid"
    | "dispute"
    | "question"
    | "complaint"
    | "unsubscribe"
    | "auto_response"
    | "other";

export type ReplyStatus =
    | "pending"
    | "processed"
    | "needs_review"
    | "failed"
    | "resolved";

export interface IReplyClassification {
    intent: ReplyIntent;
    confidence: number;
    reasons: string[];
    extracted?: {
        promisedDate?: string;
        promisedAmount?: number;
        invoiceNumber?: string;
    };
    classifierVersion: string;
    classifiedAt: string;
}

export interface ICustomerReplyAction {
    type: string;
    at: string;
    metadata?: Record<string, any>;
}

/**
 * Backend stores processedActions as a flat string[] (e.g. "created_promise",
 * "stamped_lastCustomerReplyAt"). The object shape above is reserved for a
 * future structured-action migration.
 */
export type ProcessedActionEntry = string | ICustomerReplyAction;

export interface ICustomerReply {
    _id: string;
    businessId: string;
    customerId?: string;
    invoiceId?: string;
    dunningEventId?: string;
    channel: ReplyChannel;
    fromAddress: string;
    toAddress: string;
    subject?: string;
    messageIdHeader?: string;
    inReplyToHeader?: string;
    referencesHeader?: string[];
    bodyText: string;
    bodyHtml?: string;
    rawSnippet?: string;
    receivedAt: string;
    status: ReplyStatus;
    classification?: IReplyClassification;
    processedActions?: ProcessedActionEntry[];
    processedAt?: string;
    processingError?: string;
    reviewNote?: string;
    reviewedByUserId?: string;
    reviewedAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface IListRepliesQuery {
    status?: ReplyStatus | ReplyStatus[];
    intent?: ReplyIntent | ReplyIntent[];
    invoiceId?: string;
    customerId?: string;
    cursor?: string;
    limit?: number;
}

export interface IRepliesSummary {
    total: number;
    needsReview: number;
    byStatus: Partial<Record<ReplyStatus, number>>;
    byIntent: Partial<Record<ReplyIntent, number>>;
}

export interface IResolveReplyPayload {
    note?: string;
}

export interface IManualIngestReplyPayload {
    channel?: ReplyChannel;
    fromAddress: string;
    toAddress: string;
    subject?: string;
    bodyText: string;
    bodyHtml?: string;
    messageIdHeader?: string;
    inReplyToHeader?: string;
    referencesHeader?: string[];
    receivedAt?: string;
    invoiceIdHint?: string;
}

// Human-readable labels for chips / filters in the UI.
export const REPLY_INTENT_LABELS: Record<ReplyIntent, string> = {
    payment_commitment: "Payment commitment",
    already_paid: "Already paid",
    dispute: "Dispute",
    question: "Question",
    complaint: "Complaint",
    unsubscribe: "Unsubscribe",
    auto_response: "Auto-response",
    other: "Other",
};

export const REPLY_STATUS_LABELS: Record<ReplyStatus, string> = {
    pending: "Pending",
    processed: "Processed",
    needs_review: "Needs review",
    failed: "Failed",
    resolved: "Resolved",
};

// ──────────────────────────────────────────────────────────────────────
// Stage 2I — Customer behaviour segmentation + recommendations
// ──────────────────────────────────────────────────────────────────────
export type CustomerSegment =
    | "new"
    | "reliable"
    | "occasional_late"
    | "at_risk"
    | "delinquent"
    | "unresponsive"
    | "disputer";

export const CUSTOMER_SEGMENT_LABELS: Record<CustomerSegment, string> = {
    new: "New",
    reliable: "Reliable",
    occasional_late: "Occasionally late",
    at_risk: "At risk",
    delinquent: "Delinquent",
    unresponsive: "Unresponsive",
    disputer: "In dispute",
};

export type RecommendationType =
    | "reduce_outreach"
    | "enable_early_reminder"
    | "offer_payment_plan"
    | "switch_channel"
    | "escalate_review"
    | "pause_and_review";

export const RECOMMENDATION_TYPE_LABELS: Record<RecommendationType, string> = {
    reduce_outreach: "Reduce outreach",
    enable_early_reminder: "Enable early reminder",
    offer_payment_plan: "Offer payment plan",
    switch_channel: "Switch channel",
    escalate_review: "Escalate for review",
    pause_and_review: "Pause & review dispute",
};

export type RecommendationStatus = "pending" | "applied" | "dismissed" | "expired";

export interface ICustomerBehaviorMetrics {
    invoicesTotal: number;
    invoicesPaid: number;
    invoicesOpen: number;
    invoicesOverdue: number;
    totalBilled: number;
    totalOutstanding: number;
    overdueBalance: number;
    onTimePayments: number;
    latePayments: number;
    onTimeRate: number;
    avgDaysToPay: number;
    maxDaysOverdue: number;
    promisesMade: number;
    promisesKept: number;
    promisesBroken: number;
    promiseReliability: number;
    brokenPromiseCount: number;
    disputesOpen: number;
    repliesTotal: number;
    disputeReplies: number;
    complaintReplies: number;
    lastReplyAt?: string;
    hasPhone: boolean;
    hasEmail: boolean;
}

export interface ICustomerBehavior {
    _id: string;
    businessId: string;
    customerId: string;
    customerDisplayName?: string;
    currency: string;
    metrics: ICustomerBehaviorMetrics;
    segment: CustomerSegment;
    segmentReason: string;
    attentionScore: number;
    computedAt: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface IRecommendation {
    _id: string;
    businessId: string;
    customerId: string;
    customerDisplayName?: string;
    type: RecommendationType;
    segment: CustomerSegment;
    title: string;
    rationale: string;
    suggestedAction: string;
    priority: "low" | "medium" | "high" | "urgent";
    confidence: number;
    status: RecommendationStatus;
    autoApplied: boolean;
    appliedAt?: string;
    taskId?: string;
    dismissedAt?: string;
    dismissReason?: string;
    metadata?: Record<string, any>;
    createdAt?: string;
    updatedAt?: string;
}

/** Map of segment → count (every segment present, 0 when empty). */
export type ISegmentCounts = Record<CustomerSegment, number>;

export interface IBehaviorListResponse {
    items: ICustomerBehavior[];
    total: number;
    page: number;
    limit: number;
}

export interface IRecommendationListResponse {
    items: IRecommendation[];
    total: number;
    page: number;
    limit: number;
}

export interface IRecomputeIntelligenceResult {
    behavior: {
        profiles: number;
        segments: Record<string, number>;
        computedAt: string;
    };
    recommendations: {
        created: number;
        autoApplied: number;
        skipped: number;
        expired: number;
    };
}

// ─────────────────────────────────────────────────────────────────────────
// Stage 2J — Customer Prediction Analysis.
// Mirrors server/src/collect/types/predictionEnums.ts and the
// PredictionAnalysis model. Prediction is an ESTIMATE, never a guarantee —
// the UI always surfaces `disclaimerText` and uses safer language.
// ─────────────────────────────────────────────────────────────────────────

export type PredictionType = "invoice" | "customer";
export type PredictionRiskLevel = "low" | "medium" | "high" | "critical";
export type PredictionConfidenceLevel = "low" | "medium" | "high";
export type ReasonDirection = "positive" | "negative" | "neutral";

export type PredictionReasonCode =
    | "strong_on_time_history"
    | "keeps_promises"
    | "responsive_to_outreach"
    | "low_dispute_history"
    | "normal_invoice_amount"
    | "recent_payment_improving"
    | "partial_payment_willingness"
    | "frequent_late_payments"
    | "long_average_delay"
    | "broken_promises"
    | "high_overdue_balance"
    | "unresponsive_to_outreach"
    | "dispute_history"
    | "outlier_invoice_amount"
    | "multiple_open_overdue"
    | "limited_history"
    | "new_customer"
    | "missing_due_date"
    | "stale_sync_data"
    | "low_data_quality";

export type RecoveryActionHint =
    | "reduce_outreach"
    | "early_reminder"
    | "standard_reminders"
    | "offer_payment_plan"
    | "switch_channel"
    | "escalate_review"
    | "pause_dispute_review"
    | "gentle_first_invoice";

export type PredictionChartType =
    | "payment_reliability_trend"
    | "predicted_probability"
    | "receivables_timeline"
    | "whatif_cash_impact"
    | "accountability_breakdown";

/** Plain-English risk labels (safer language — not a credit score). */
export const PREDICTION_RISK_LEVEL_LABELS: Record<PredictionRiskLevel, string> = {
    low: "Low risk",
    medium: "Medium risk",
    high: "High risk",
    critical: "Critical risk",
};

export const PREDICTION_CONFIDENCE_LABELS: Record<
    PredictionConfidenceLevel,
    string
> = {
    low: "Low confidence",
    medium: "Medium confidence",
    high: "High confidence",
};

export const RECOVERY_ACTION_LABELS: Record<RecoveryActionHint, string> = {
    reduce_outreach: "Ease off reminders",
    early_reminder: "Start an early reminder sequence before the due date",
    standard_reminders: "Keep standard reminder cadence",
    offer_payment_plan: "Offer a payment plan",
    switch_channel: "Try a different outreach channel",
    escalate_review: "Escalate to manual review",
    pause_dispute_review: "Pause automated dunning and review the dispute",
    gentle_first_invoice: "Send a gentle first-invoice follow-up",
};

/** Editable what-if scenario inputs. Only set values override the baseline. */
export interface IScenarioInputs {
    invoiceAmount?: number;
    dueDate?: string;
    paymentTermsDays?: number;
    expectedPaymentDelayDays?: number;
    recoveryIntensity?: number;
    reminderLeadDays?: number;
    smsEnabled?: boolean;
    emailEnabled?: boolean;
    promiseToPayDate?: string;
    customerRiskAdjustment?: number;
    expectedPartialPaymentAmount?: number;
    expectedFullPaymentDate?: string;
    applyLateFee?: boolean;
    markDisputed?: boolean;
    markNonResponsive?: boolean;
    excludeBrokenPromises?: boolean;
    excludePriorInvoices?: boolean;
}

export interface IPredictionReasonEntry {
    code: PredictionReasonCode;
    direction: ReasonDirection;
    explanation: string;
}

/** Core forecast numbers shared by the baseline and each scenario. */
export interface IForecastFigures {
    confidenceScore: number;
    onTimeProbability: number;
    lateProbability: number;
    nonPaymentRiskProbability: number;
    expectedDaysToPay: number;
    expectedDaysLate: number;
    expectedPaymentDate?: string;
    expectedPaymentDateMin?: string;
    expectedPaymentDateMax?: string;
    projectedRecoveredAmount: number;
    projectedOutstandingAmount: number;
    projectedCashImpact: number;
    customerRiskLevel: PredictionRiskLevel;
}

export interface IPredictionScenario {
    _id?: string;
    name: string;
    inputs: IScenarioInputs;
    figures: IForecastFigures;
    recommendedRecoveryAction: RecoveryActionHint;
    cashImpactDelta: number;
    createdByUserId?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface IPredictionChart {
    type: PredictionChartType;
    data: Record<string, any>;
}

export interface IPredictionAnalysis {
    _id: string;
    businessId: string;
    customerId: string;
    customerDisplayName?: string;
    invoiceId?: string;
    invoiceDocNumber?: string;
    sourceRecommendationId?: string;
    predictionType: PredictionType;
    generatedAt: string;
    generatedByUserId?: string;
    modelVersion: string;
    dataSnapshotId: string;

    confidenceScore: number;
    confidenceLevel: PredictionConfidenceLevel;
    onTimeProbability: number;
    lateProbability: number;
    nonPaymentRiskProbability: number;
    expectedPaymentDate?: string;
    expectedPaymentDateMin?: string;
    expectedPaymentDateMax?: string;
    expectedDaysToPay: number;
    expectedDaysLate: number;
    projectedRecoveredAmount: number;
    projectedOutstandingAmount: number;
    projectedCashImpact: number;
    customerRiskLevel: PredictionRiskLevel;

    reliabilityScore: number;
    recommendedRecoveryAction: RecoveryActionHint;
    recommendedWorkflowId?: string;

    reasonCodes: IPredictionReasonEntry[];
    summary: string;
    confidenceExplanation: string;

    assumptions: IScenarioInputs;
    scenarios: IPredictionScenario[];
    chartData: IPredictionChart[];
    dataSnapshot: Record<string, any>;

    disclaimerText: string;
    deletedAt?: string;
    createdAt?: string;
    updatedAt?: string;
}

/** Live what-if preview result (server does NOT persist this). */
export interface ISimulationResult {
    figures: IForecastFigures;
    recommendedRecoveryAction: RecoveryActionHint;
    reasonCodes: IPredictionReasonEntry[];
    cashImpactDelta: number;
    onTimeProbabilityDelta: number;
}

export interface IPredictionListResponse {
    items: IPredictionAnalysis[];
    total: number;
    page: number;
    limit: number;
}

/** Chart-data-only payload returned by GET /predictions/:id/chart-data. */
export interface IPredictionChartDataResponse {
    chartData: IPredictionChart[];
    disclaimerText: string;
}

/** Result of applying a prediction's recommended recovery action. */
export interface IApplyPredictionResult {
    prediction: IPredictionAnalysis;
    recommendationApplied: boolean;
    taskId?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Collect subscription & billing (Story 1 backend → Story 2 UI).
// Mirrors server/src/collect/models/collectSubscriptionSchema.ts. All dates
// arrive as ISO strings over the wire.
// ─────────────────────────────────────────────────────────────────────────

export type CollectCurrency = "usd" | "cad" | "gbp";

export type CollectSubscriptionStatus =
    | "trialing"
    | "trial_expired"
    | "active"
    | "pending"
    | "past_due"
    | "pending_cancellation"
    | "cancelled"
    | "inactive";

export type CollectPlanPhase = "intro" | "standard";

export type CollectRetentionState = "retained" | "purged" | "n/a";

export interface ICollectSubscription {
    _id: string;
    businessId: string;

    stripeCustomerId?: string;
    stripeSubscriptionId?: string;
    stripeScheduleId?: string;

    billingCountry?: string;
    currency?: CollectCurrency;

    /** Display-safe default-card snapshot (never the full card number). */
    cardBrand?: string;
    cardLast4?: string;
    cardExpMonth?: number;
    cardExpYear?: number;

    planPhase: CollectPlanPhase;
    introPriceId?: string;
    standardPriceId?: string;
    introChargesRemaining?: number;
    introEndsAt?: string;

    status: CollectSubscriptionStatus;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd: boolean;
    cancellationReason?: string;
    canceledAt?: string;

    trialStartedAt?: string;
    trialEndsAt?: string;

    reactivationWindowEndsAt?: string;
    retentionState: CollectRetentionState;

    createdAt?: string;
    updatedAt?: string;
}

/** POST create/resubscribe response — subscription plus SCA hint. */
export interface ICollectSubscriptionResult {
    subscription: ICollectSubscription;
    /** True when the first charge needs an SCA/3-D Secure challenge. */
    requiresAction: boolean;
    /** Present when requiresAction — pass to stripe.confirmCardPayment. */
    clientSecret?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Collect access (Story 5) — the backend's authoritative answer to
// "can this principal use Collect right now?". Mirrors
// server/src/collect/services/collectAccessService.ts. Dates are ISO strings.
// ─────────────────────────────────────────────────────────────────────────

export type CollectAccessState =
    | "super_admin"
    | "trialing"
    | "trial_expired"
    | "active"
    | "pending"
    | "past_due"
    | "past_due_expired"
    | "pending_cancellation"
    | "cancelled"
    | "inactive";

export interface ICollectAccess {
    state: CollectAccessState;
    /** True when the principal may use premium Collect features now. */
    hasAccess: boolean;
    /** True for the platform super-admin (free, never-expiring access). */
    isSuperAdmin: boolean;
    /** True while inside the free trial window. */
    isTrialing: boolean;
    trialDaysRemaining?: number;
    trialEndsAt?: string;
    currentPeriodEnd?: string;
    reactivationWindowEndsAt?: string;
    retentionState?: CollectRetentionState;
    subscriptionStatus?: CollectSubscriptionStatus;
}

/** GET /collect/subscription response — subscription plus access summary. */
export interface ICollectSubscriptionWithAccess {
    subscription: ICollectSubscription | null;
    access: ICollectAccess | null;
}

export interface ICreateCollectSubscriptionPayload {
    businessId?: string;
    /** Optional only for a one-click resubscribe that reuses the retained PM. */
    paymentMethodId?: string;
}

export interface ICancelCollectSubscriptionPayload {
    businessId?: string;
    reason?: string;
    /** Default false → cancel at period end. True → terminate immediately. */
    immediate?: boolean;
}

export interface IUpdateCollectPaymentMethodPayload {
    businessId?: string;
    paymentMethodId: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Help & Support — in-app tickets + super-admin Incident Queue
// ─────────────────────────────────────────────────────────────────────────

export type SupportTicketCategory =
    | "technical"
    | "billing"
    | "quickbooks_sync"
    | "account"
    | "feature_request"
    | "other";

export type SupportTicketPriority = "low" | "normal" | "high" | "urgent";

export type SupportTicketStatus =
    | "open"
    | "in_progress"
    | "waiting_on_user"
    | "resolved"
    | "closed"
    | "reopened";

export type SupportPriorityTier = "low" | "medium" | "high" | "urgent";

export interface ISupportStatusHistoryEntry {
    from: SupportTicketStatus | null;
    to: SupportTicketStatus;
    actorType: "user" | "agent" | "system";
    note: string | null;
    at: string;
}

export interface ISupportSubscriptionSnapshot {
    status?: string;
    planPhase?: string;
    supportPriority: SupportPriorityTier;
}

export interface ISupportTicket {
    _id: string;
    ticketNumber: string;
    businessId: string;
    submitterName: string | null;
    submitterEmail: string;
    category: SupportTicketCategory;
    priority: SupportTicketPriority;
    subject: string;
    description: string;
    status: SupportTicketStatus;
    statusHistory: ISupportStatusHistoryEntry[];
    resolutionNote: string | null;
    resolvedAt: string | null;
    lastStatusChangeAt: string | null;
    lastReplyAt: string | null;
    createdAt: string;
    updatedAt: string;

    // Admin-only fields (never populated for end-user views).
    assignedToBusinessUserId?: string | null;
    subscriptionSnapshot?: ISupportSubscriptionSnapshot;
    supportPriority?: SupportPriorityTier;
    diagnostics?: {
        appPath?: string;
        userAgent?: string;
        appVersion?: string;
    } | null;
}

export interface ISupportTicketMessage {
    _id: string;
    ticketId: string;
    authorType: "user" | "agent" | "system";
    authorName: string | null;
    body: string;
    isInternalNote: boolean;
    createdAt: string;
}

export interface ISupportLiveSubscription {
    status: string | null;
    planPhase: string | null;
    tierLabel: string;
    supportPriority: SupportPriorityTier;
}

/** Admin queue row — ticket enriched with the live subscription tier. */
export interface IAdminSupportTicketRow extends ISupportTicket {
    businessName: string;
    liveSubscription: ISupportLiveSubscription;
}

export interface ISupportTicketListPage {
    items: ISupportTicket[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

export interface IAdminSupportTicketListPage {
    items: IAdminSupportTicketRow[];
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

/** End-user ticket detail (thread excludes internal notes). */
export interface ISupportTicketDetail {
    ticket: ISupportTicket;
    messages: ISupportTicketMessage[];
}

/** Admin ticket detail — full context incl. internal notes + live tier. */
export interface IAdminSupportTicketDetail {
    ticket: ISupportTicket;
    messages: ISupportTicketMessage[];
    business: { _id: string; name: string; isActive: boolean | null };
    liveSubscription: ISupportLiveSubscription;
    assignee: { _id: string; name: string; email: string } | null;
}

export interface ICreateSupportTicketPayload {
    category: SupportTicketCategory;
    priority?: SupportTicketPriority;
    subject: string;
    description: string;
    diagnostics?: {
        appPath?: string;
        userAgent?: string;
        appVersion?: string;
    };
}

export interface IAdminSupportListQuery {
    status?: SupportTicketStatus;
    category?: SupportTicketCategory;
    priority?: SupportTicketPriority;
    tier?: SupportPriorityTier;
    businessId?: string;
    search?: string;
    page?: number;
    limit?: number;
}

export interface IAdminUpdateSupportTicketPayload {
    status?: SupportTicketStatus;
    priority?: SupportTicketPriority;
    assignedToBusinessUserId?: string | null;
    note?: string;
}

